// A column header's menu: for managers on a list column, Manage this list first; then sort, back
// to priority order, and "Show values" (a filter on the column; not on Source, the toolbar filters it).

import { useState } from "react";
import type { ReactNode } from "react";
import { headerValueMatches } from "@app/shared";
import { Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger } from "@axny/platform-ui";
import type { ColumnDef } from "./columns";

export function HeaderMenu({
  column,
  title,
  sorted,
  onSort,
  values: valuesOf,
  kept,
  onKeep,
  onManage,
  children,
}: {
  column: ColumnDef;
  title: string;
  sorted: false | "asc" | "desc" | undefined;
  onSort: (dir: "asc" | "desc" | null) => void;
  /**
   * Worked out when the menu opens: every row's value, so not on each render. `label` is how the
   * list says it, when the cell shows something else ("ROSSSTORES — ROSS STORES").
   */
  values: () => { value: string; label?: string }[];
  kept: string[];
  onKeep: (values: string[]) => void;
  onManage?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [ticked, setTicked] = useState<string[]>(kept);
  const [values, setValues] = useState<{ value: string; label?: string }[]>([]);
  const isOrdered = column.sortValue !== undefined || column.align === "right";
  // Found by what it says or by its value: a code or an abbreviation, or the name.
  const shown = values.filter((v) => headerValueMatches(v, search)).slice(0, 200);
  const label = (v: { value: string; label?: string }) => v.label || v.value || "(empty)";

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setValues(valuesOf());
          setTicked(kept);
          setSearch("");
        }
      }}
    >
      <PopoverTrigger
        title={title}
        className="flex h-full w-full min-w-0 items-center gap-1 px-2 text-left text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 gap-1 p-1.5 text-[13px]"
        // Rendered in a portal but inside the grid in React: its keys and pastes would reach the
        // grid, which takes a typed letter as the start of an edit — the search would never see them.
        onKeyDown={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
      >
        <p className="text-muted-foreground px-2 pt-1 text-[11px]">{title}</p>
        {onManage ? (
          <div className="border-b pb-1">
            <MenuButton
              title="Add, edit, retire, delete or merge this list's values"
              onClick={() => {
                setOpen(false);
                onManage();
              }}
            >
              <span className="block truncate font-medium">Manage this list…</span>
            </MenuButton>
          </div>
        ) : null}
        <MenuButton active={sorted === "asc"} onClick={() => (onSort("asc"), setOpen(false))}>
          {isOrdered ? "Earliest / smallest first" : "A → Z"}
        </MenuButton>
        <MenuButton active={sorted === "desc"} onClick={() => (onSort("desc"), setOpen(false))}>
          {isOrdered ? "Latest / largest first" : "Z → A"}
        </MenuButton>
        <MenuButton onClick={() => (onSort(null), setOpen(false))}>Back to priority order</MenuButton>
        {column.noFilter ? (
          <p className="text-muted-foreground mt-1 border-t px-2 pt-1.5 text-[11px]">
            Filter it with Source above the grid.
          </p>
        ) : (
          <div className="mt-1 border-t pt-1.5">
            <p className="px-2 pb-1 text-xs font-semibold">Show values</p>
            <Input
              className="mb-1 h-7 text-xs"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="header-values-search"
            />
            <div className="max-h-56 overflow-y-auto">
              {!shown.length ? <p className="text-muted-foreground px-2 py-1 text-xs">No match</p> : null}
              {shown.map((v) => (
                <label
                  key={v.value}
                  className="hover:bg-muted flex cursor-pointer items-start gap-2 rounded px-2 py-1"
                  title={label(v)}
                >
                  <Checkbox
                    checked={ticked.includes(v.value)}
                    onCheckedChange={(c) => setTicked((t) => (c ? [...t, v.value] : t.filter((x) => x !== v.value)))}
                  />
                  {/* Wrapped, never cut: a division's code is at the start, its source at the end. */}
                  <span className="min-w-0 break-words">{label(v)}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 px-1 pt-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onKeep([]);
                  setOpen(false);
                }}
              >
                ✕ Clear filter
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onKeep(ticked);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function MenuButton({
  active,
  title,
  onClick,
  children,
}: {
  active?: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      className="hover:bg-muted w-full rounded px-2 py-1.5 text-left data-[active=true]:font-semibold"
      data-active={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
